/**
 * ByOM.jsx — Vista detalhada por Operational Manager.
 */

import React, { useEffect, useState } from "react";
import { fetchOperationalManagers, fetchOperationalManager } from "../services/api";
import { formatDate } from "../utils/formatDate";
import SearchableSelect from "./SearchableSelect";

const PIE_CORES = [
  "#6366f1","#f97316","#22c55e","#ef4444","#2563eb","#d97706",
  "#8b5cf6","#ec4899","#14b8a6","#f59e0b","#64748b","#10b981",
];

const DEADLINE_GRUPOS_CONFIG = {
  ultrapassado:  { cor: "#dc2626", fundo: "#fff1f2", borda: "#fecaca", icone: "🔴", label: "Prazo Ultrapassado" },
  hoje:          { cor: "#ea580c", fundo: "#fff7ed", borda: "#fed7aa", icone: "🟠", label: "Para Hoje" },
  amanha:        { cor: "#d97706", fundo: "#fffbeb", borda: "#fde68a", icone: "🟡", label: "Para Amanhã" },
  depois_amanha: { cor: "#2563eb", fundo: "#eff6ff", borda: "#bfdbfe", icone: "🔵", label: "Depois de Amanhã" },
  sem_deadline:  { cor: "#64748b", fundo: "#f8fafc", borda: "#e2e8f0", icone: "⚪", label: "Sem Deadline" },
};

function agruparPorDeadline(tarefas) {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const amanha = new Date(hoje); amanha.setDate(amanha.getDate() + 1);
  const depoisAmanha = new Date(hoje); depoisAmanha.setDate(depoisAmanha.getDate() + 2);

  const grupos = { ultrapassado: [], hoje: [], amanha: [], depois_amanha: [], sem_deadline: [] };
  for (const t of tarefas) {
    if (!t.due_date) {
      grupos.sem_deadline.push(t);
    } else {
      const d = new Date(t.due_date);
      d.setHours(0, 0, 0, 0);
      if (d < hoje)                                    grupos.ultrapassado.push(t);
      else if (d.getTime() === hoje.getTime())         grupos.hoje.push(t);
      else if (d.getTime() === amanha.getTime())       grupos.amanha.push(t);
      else if (d.getTime() === depoisAmanha.getTime()) grupos.depois_amanha.push(t);
      else                                             grupos.sem_deadline.push(t);
    }
  }
  return grupos;
}

const BUCKETS_ORDEM = [
  "ROADMAP", "NEGOCIAR DEADLINE", "ONGOING",
  "AGUARDO FEEDBACK", "PARA APROVAÇÃO INTERNA",
  "PARA APROVAÇÃO EXTERNA", "APROVADO", "CONCLUÍDO",
];

export default function ByOM() {
  const [oms, setOms]                 = useState([]);
  const [omSelecionado, setOmSelecionado] = useState("");
  const [data, setData]               = useState(null);
  const [loadingLista, setLoadingLista] = useState(true);
  const [loadingDados, setLoadingDados] = useState(false);
  const [erro, setErro]               = useState(null);
  const [expandidosDeadline, setExpandidosDeadline] = useState({
    ultrapassado: false, hoje: false, amanha: false, depois_amanha: false, sem_deadline: false,
  });
  const [filtroBucket, setFiltroBucket] = useState("");

  useEffect(() => {
    fetchOperationalManagers()
      .then((lista) => {
        setOms(lista);
        if (lista.length === 1) handleSeleccao(String(lista[0].id));
      })
      .catch((e) => setErro(e.message))
      .finally(() => setLoadingLista(false));
  }, []);

  const handleSeleccao = (omId) => {
    setOmSelecionado(omId);
    if (!omId) { setData(null); return; }
    setLoadingDados(true);
    setErro(null);
    setData(null);
    fetchOperationalManager(omId)
      .then(setData)
      .catch((e) => setErro(e.message))
      .finally(() => setLoadingDados(false));
  };

  return (
    <div>
      <h1 style={styles.titulo}>Por Operational Manager</h1>

      <div style={styles.selectorBar}>
        {loadingLista ? (
          <p style={styles.loading}>A carregar…</p>
        ) : (
          <SearchableSelect
            options={[...oms]
              .sort((a, b) => (a.tipo === b.tipo ? 0 : a.tipo === "apoio" ? 1 : -1))
              .map((om) => ({
                value: om.id,
                label: om.nome,
                group: om.tipo === "apoio" ? "Apoio" : "Operational Managers",
              }))}
            value={omSelecionado}
            onChange={handleSeleccao}
            placeholder="Seleccionar Operational Manager…"
          />
        )}
      </div>

      {erro && <div style={styles.erro}>{erro}</div>}
      {loadingDados && <p style={styles.loading}>A carregar dados…</p>}

      {data && (
        <>
          <div style={styles.cabecalho}>
            <div>
              <h2 style={styles.subtitulo}>{data.om_nome}</h2>
              {data.om_email && <p style={styles.email}>{data.om_email}</p>}
            </div>
            {data.project_managers?.length > 0 && (
              <div style={styles.pmSection}>
                <span style={styles.pmLabel}>Project Managers:</span>
                <div style={styles.pmTags}>
                  {data.project_managers.map((pm) => (
                    <span key={pm.id} style={styles.pmTag}>{pm.nome}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* KPIs */}
          <div className="grid-kpi-4" style={styles.kpiGrid}>
            {[
              ["Total Ativas",     data.kpis.total_ativas,      "#6366f1"],
              ["Deadline Próximo", data.kpis.deadline_proximo,  "#f97316"],
              ["Aguardo Info",     data.kpis.aguardo_info,      "#f97316"],
              ["Aprov. Externa",   data.kpis.aprovacao_externa, "#2563eb"],
            ].map(([label, valor, cor]) => (
              <div key={label} style={{ ...styles.kpiCard, borderTop: `4px solid ${cor}` }}>
                <span style={{ ...styles.kpiValor, color: cor }}>{valor}</span>
                <span style={styles.kpiLabel}>{label}</span>
              </div>
            ))}
          </div>

          {/* Pipeline + Distribuição por Marca */}
          <div className="duas-colunas" style={styles.duasColunas}>
            {Object.keys(data.pipeline).length > 0 && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitulo}>Pipeline</h3>
                <div className="grid-pipeline" style={styles.pipelineGrid}>
                  {BUCKETS_ORDEM.map((b) => (
                    <div key={b} style={styles.bucketCard}>
                      <span style={styles.bucketNome}>{b}</span>
                      <span style={styles.bucketTotal}>{data.pipeline[b] ?? 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={styles.section}>
              <h3 style={styles.sectionTitulo}>Distribuição por Marca</h3>
              {data.distribuicao_por_marca.length === 0 ? (
                <p style={styles.semDados}>Sem dados.</p>
              ) : (
                <div style={styles.cssBarras}>
                  {data.distribuicao_por_marca.map((m, i) => (
                    <div
                      key={m.marca}
                      style={styles.cssBarraLinha}
                      title={m.inativo ? "Sem tarefas ativas" : undefined}
                    >
                      <span style={m.inativo ? { ...styles.cssBarraNome, ...styles.cssBarraNomeInativa } : styles.cssBarraNome}>
                        {m.marca}
                      </span>
                      <div style={styles.cssBarraFundo}>
                        <div style={{
                          ...styles.cssBarraFill,
                          width: `${m.percentagem}%`,
                          backgroundColor: m.inativo ? "#cbd5e1" : PIE_CORES[i % PIE_CORES.length],
                        }} />
                      </div>
                      <span style={m.inativo ? { ...styles.cssBarraTotal, ...styles.cssBarraTotalInativa } : styles.cssBarraTotal}>
                        {m.total}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Detalhe de tarefas agrupado por deadline */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h3 style={{ ...styles.sectionTitulo, margin: 0 }}>Detalhe de Tarefas</h3>
              {data.tarefas_deadline_proximo.length > 0 && (
                <select
                  style={styles.filtroBucketSelect}
                  value={filtroBucket}
                  onChange={(e) => setFiltroBucket(e.target.value)}
                >
                  <option value="">Todos os estados</option>
                  {[...new Set(data.tarefas_deadline_proximo.map((t) => t.bucket_name).filter(Boolean))]
                    .sort()
                    .map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              )}
            </div>
            {data.tarefas_deadline_proximo.length === 0 ? (
              <p style={styles.semDados}>Sem tarefas com deadline.</p>
            ) : (
              <div style={styles.deadlineGrupos}>
                {Object.entries(agruparPorDeadline(
                  filtroBucket
                    ? data.tarefas_deadline_proximo.filter((t) => t.bucket_name === filtroBucket)
                    : data.tarefas_deadline_proximo
                )).map(([key, tarefas]) => {
                  const cfg    = DEADLINE_GRUPOS_CONFIG[key];
                  const aberto = expandidosDeadline[key];
                  return (
                    <div key={key} style={{ ...styles.deadlineGrupoCard, borderColor: cfg.borda }}>
                      <button
                        style={{ ...styles.deadlineGrupoHeader, backgroundColor: cfg.fundo, borderBottomColor: aberto ? cfg.borda : "transparent" }}
                        onClick={() => setExpandidosDeadline((prev) => ({ ...prev, [key]: !prev[key] }))}
                      >
                        <div style={styles.deadlineGrupoLeft}>
                          <span>{cfg.icone}</span>
                          <span style={{ ...styles.deadlineGrupoLabel, color: cfg.cor }}>{cfg.label}</span>
                          <span style={{ ...styles.deadlineGrupoBadge, backgroundColor: cfg.cor }}>{tarefas.length}</span>
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: cfg.cor }}>{aberto ? "▲" : "▼"}</span>
                      </button>
                      {aberto && (
                        tarefas.length === 0 ? (
                          <p style={styles.semTarefasGrupo}>Nenhuma tarefa neste grupo.</p>
                        ) : (
                          <div className="table-scroll">
                            <table style={styles.tabela}>
                              <thead>
                                <tr>
                                  <th style={styles.th}>Marca</th>
                                  <th style={styles.th}>Tarefa</th>
                                  <th style={styles.th}>Bucket</th>
                                  <th style={styles.th}>Deadline</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tarefas.map((t) => (
                                  <tr key={t.task_id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                    <td style={{ ...styles.td, fontWeight: 600, whiteSpace: "nowrap" }}>{t.marca}</td>
                                    <td style={styles.td}>{t.title}</td>
                                    <td style={styles.td}>
                                      <span style={styles.bucketTag}>{t.bucket_name}</span>
                                    </td>
                                    <td style={{ ...styles.td, fontWeight: 500, color: cfg.cor }}>{formatDate(t.due_date)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  titulo:    { fontSize: 24, fontWeight: 700, marginBottom: 24 },
  subtitulo: { fontSize: 18, fontWeight: 600, margin: 0, color: "#374151" },
  email:     { fontSize: 13, color: "#94a3b8", margin: "4px 0 0" },
  cabecalho: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 16 },
  pmSection: { display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" },
  pmLabel:   { fontSize: 12, color: "#64748b", fontWeight: 600 },
  pmTags:    { display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" },
  pmTag:     { background: "#e0e7ff", color: "#4338ca", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 500 },
  selectorBar: { marginBottom: 24 },
  kpiGrid:   { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 },
  kpiCard:   { background: "#fff", borderRadius: 8, padding: "20px 24px", boxShadow: "0 1px 3px rgba(0,0,0,.08)", display: "flex", flexDirection: "column", gap: 10 },
  kpiValor:  { fontSize: 32, fontWeight: 700, lineHeight: 1 },
  kpiLabel:  { fontSize: 13, fontWeight: 600, color: "#374151" },
  section:   { background: "#fff", borderRadius: 8, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,.08)", marginBottom: 20, minWidth: 0 },
  duasColunas:  { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 0 },
  sectionTitulo: { fontSize: 15, fontWeight: 600, margin: "0 0 16px", color: "#1e293b" },
  sectionHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  filtroBucketSelect: { padding: "6px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12, color: "#374151", backgroundColor: "#fff", cursor: "pointer" },
  pipelineGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 },
  bucketCard:   { border: "1px solid #e2e8f0", borderRadius: 6, padding: "10px 14px", display: "flex", flexDirection: "column", gap: 2 },
  bucketNome:   { fontSize: 11, color: "#64748b" },
  bucketTotal:  { fontSize: 20, fontWeight: 700, color: "#1e293b" },
  cssBarras:    { display: "flex", flexDirection: "column", gap: 7 },
  cssBarraLinha: { display: "flex", alignItems: "center", gap: 10 },
  cssBarraNome:  { fontSize: 12, color: "#374151", width: 170, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  cssBarraNomeInativa: { color: "#94a3b8", fontStyle: "italic" },
  cssBarraFundo: { flex: 1, height: 14, backgroundColor: "#f1f5f9", borderRadius: 3, overflow: "hidden" },
  cssBarraFill:  { height: "100%", borderRadius: 3 },
  cssBarraTotal: { fontSize: 12, color: "#64748b", width: 28, textAlign: "right", flexShrink: 0 },
  cssBarraTotalInativa: { color: "#94a3b8" },
  deadlineGrupos:     { display: "flex", flexDirection: "column", gap: 8 },
  deadlineGrupoCard:  { border: "1px solid", borderRadius: 8, overflow: "hidden", backgroundColor: "#fff" },
  deadlineGrupoHeader: { width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", border: "none", borderBottom: "1px solid", cursor: "pointer", textAlign: "left" },
  deadlineGrupoLeft:  { display: "flex", alignItems: "center", gap: 8 },
  deadlineGrupoLabel: { fontSize: 13, fontWeight: 700 },
  deadlineGrupoBadge: { color: "#fff", fontSize: 11, fontWeight: 700, borderRadius: 10, padding: "2px 8px", minWidth: 22, textAlign: "center" },
  semTarefasGrupo:    { padding: "14px 16px", color: "#94a3b8", fontSize: 13, margin: 0 },
  tabela:    { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:        { textAlign: "left", padding: "8px 12px", backgroundColor: "#f8fafc", fontWeight: 600, borderBottom: "2px solid #e2e8f0", color: "#475569" },
  td:        { padding: "8px 12px", color: "#374151", borderBottom: "1px solid #f1f5f9" },
  bucketTag: { display: "inline-block", padding: "2px 8px", background: "#f1f5f9", color: "#475569", borderRadius: 4, fontSize: 11, fontWeight: 500 },
  semDados:  { color: "#94a3b8", fontSize: 14, margin: 0 },
  loading:   { color: "#64748b", fontSize: 14 },
  erro:      { background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: 16, color: "#dc2626", marginBottom: 16 },
};
